import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Save, Trash2, Lock } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { metricProfilesAPI } from '../../lib/api';
import { profileLabel } from './profileUtils';

/**
 * Full CRUD over the caller's own named metric profiles. "Update" writes the
 * assumptions currently on screen into the selected profile — loaded values
 * are never written back implicitly.
 *
 * The three built-in industry starters are platform-owned: they list here for
 * reference but only Duplicate is offered, which is the "save as new profile"
 * path (and the copy keeps any modeling disclaimer).
 */
export const MetricProfilesDialog = ({ open, onOpenChange, profiles, currentValues, onChanged, suggestedName }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [busy, setBusy] = useState('');

    useEffect(() => {
        if (open) setName(suggestedName || '');
    }, [open, suggestedName]);

    const payload = (profileName) => ({
        profile_name: profileName,
        value_per_review: parseFloat(currentValues.value_per_review) || 0,
        sales_impact_pct_per_star: parseFloat(currentValues.sales_impact_pct_per_star) || 0,
        negative_review_cost: parseFloat(currentValues.negative_review_cost) || 0,
        assumed_negative_rate_pct: parseFloat(currentValues.assumed_negative_rate_pct) || 0,
        currency: currentValues.currency || 'USD',
        disclaimer: currentValues.disclaimer || null,
    });

    const run = async (key, fn, successKey, fallback) => {
        setBusy(key);
        try {
            await fn();
            toast.success(t(successKey, fallback));
            await onChanged();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesCalc.profiles.errors.save', 'Could not save the profile'));
        } finally {
            setBusy('');
        }
    };

    const create = () => {
        if (!name.trim()) {
            toast.error(t('salesCalc.profiles.errors.name', 'Give the profile a name first'));
            return;
        }
        run('create', async () => {
            await metricProfilesAPI.create(payload(name.trim()));
            setName('');
        }, 'salesCalc.profiles.saved', 'Profile saved');
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="calc-profiles-dialog">
                <DialogHeader>
                    <DialogTitle>{t('salesCalc.profiles.title', 'Metric profiles')}</DialogTitle>
                    <DialogDescription>{t('salesCalc.profiles.builtinHint', 'Built-in industry starters everyone gets — load one, then adjust or save your own copy.')}</DialogDescription>
                </DialogHeader>

                <div className="flex items-end gap-2">
                    <div className="flex-1 space-y-1.5">
                        <Label htmlFor="calc-profile-name">{t('salesCalc.profiles.nameLabel', 'Profile name')}</Label>
                        <Input id="calc-profile-name" value={name}
                            placeholder={t('salesCalc.profiles.namePlaceholder', 'e.g. Medical, Auto, Hospitality')}
                            maxLength={60}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="calc-profile-name-input" />
                    </div>
                    <Button onClick={create} disabled={busy === 'create'} data-testid="calc-profile-create-btn">
                        <Save className="w-4 h-4 mr-1.5" />{t('salesCalc.profiles.save', 'Save')}
                    </Button>
                </div>

                <div className="divide-y max-h-[320px] overflow-y-auto" data-testid="calc-profiles-list">
                    {!profiles.length ? (
                        <p className="text-sm text-muted-foreground py-3" data-testid="calc-profiles-empty">
                            {t('salesCalc.profiles.empty', 'No profiles saved yet.')}
                        </p>
                    ) : profiles.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`calc-profile-row-${p.id}`}>
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate text-slate-900 dark:text-white flex items-center gap-1.5">
                                    {profileLabel(p, t)}
                                    {p.is_builtin ? (
                                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
                                            data-testid={`calc-profile-builtin-badge-${p.builtin_key}`}>
                                            <Lock className="w-2.5 h-2.5" />
                                            {t('salesCalc.profiles.builtinBadge', 'Built-in')}
                                        </span>
                                    ) : null}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {p.currency} {Number(p.value_per_review).toFixed(2)} · {p.sales_impact_pct_per_star}% / ★ · {p.currency} {Number(p.negative_review_cost).toFixed(2)} · {p.assumed_negative_rate_pct}%
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                {p.is_builtin ? (
                                    <span className="text-[11px] text-muted-foreground max-w-[190px] text-right">
                                        {t('salesCalc.profiles.builtinReadonly', "Built-in industry profiles can't be edited or deleted.")}
                                    </span>
                                ) : (
                                    <Button size="sm" variant="outline" disabled={!!busy}
                                        onClick={() => run(`u-${p.id}`, () => metricProfilesAPI.update(p.id, payload(p.profile_name)),
                                            'salesCalc.profiles.updated', 'Profile updated')}
                                        data-testid={`calc-profile-update-${p.id}`}>
                                        {t('salesCalc.profiles.update', 'Update')}
                                    </Button>
                                )}
                                <Button size="sm" variant="ghost" disabled={!!busy}
                                    title={t('salesCalc.profiles.duplicate', 'Duplicate')}
                                    onClick={() => run(`d-${p.id}`, () => metricProfilesAPI.duplicate(p.id),
                                        'salesCalc.profiles.duplicated', 'Profile duplicated')}
                                    data-testid={`calc-profile-duplicate-${p.id}`}>
                                    <Copy className="w-4 h-4" />
                                </Button>
                                {p.is_builtin ? null : (
                                    <Button size="sm" variant="ghost" disabled={!!busy}
                                        onClick={() => {
                                            // eslint-disable-next-line no-restricted-globals
                                            if (!window.confirm(t('salesCalc.profiles.confirmDelete', 'Delete this profile?'))) return;
                                            run(`x-${p.id}`, () => metricProfilesAPI.remove(p.id),
                                                'salesCalc.profiles.deleted', 'Profile deleted');
                                        }}
                                        data-testid={`calc-profile-delete-${p.id}`}>
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default MetricProfilesDialog;
